import { styled } from '@mui/material/styles';

import {
    containerLargeWidth,
    rem,
    remToGapMultiplier,
    Theme,
} from '../../theme';
import { Heading } from '../Heading';
import { HorizontalRule } from '../HorizontalRule';
import { Paragraph } from '../Paragraph';
import { Tabs } from '../Tabs';

interface OpeningTimesProperties {
    $iconWidth: number;
}

export const StyledWrapper = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        max-width: 100%;
        margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};

        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(containerLargeWidth), containerLargeWidth * remToGapMultiplier)};
        }
	`}
`;

export const StyledRichTextContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing([rem(0.5), rem(3), rem(0.5), rem(3.5)], [1.25, 7.5, 1.25, 8.75])};

        ${theme.breakpoints.up('desktop')} {
            padding: ${theme.tggSpacing([rem(2.2), rem(3)], [9, 7.5])};
        }

        ul {
            ${theme.breakpoints.up('desktop')} {
                padding: ${theme.tggSpacing(0, 0)};
                margin: ${theme.tggSpacing(0, 0)};
                text-align: center;
                list-style: none;
            }
        }
	`}

    li {
        ${({ theme }: { theme: Theme }) => theme.typography.bodySmall}

        ${({ theme }: { theme: Theme }) => `
            margin-bottom: ${theme.tggSpacing(rem(1), 2.5)};
            ${theme.breakpoints.up('desktop')} {
                position: relative;
                display: block;
                width: fit-content;
                margin: ${theme.tggSpacing([0, 'auto', rem(2)], [0, 'auto', 5])};

                &::before {
                    position: absolute;
                    top: ${theme.tggSpacing(0, 0)};
                    left: ${theme.tggSpacing(rem(-1.5), -3.75)};
                    font-size: ${theme.tggSpacing(rem(2.3), 9)};
                    line-height: 1;
                    color: ${theme.palette.primary.main};
                    content: '•';
                }
            }

            p {
                display: inline;
            }
	    `}
    }
`;

export const StyledHorizontalRule = styled(HorizontalRule)`
    ${({ theme }: { theme: Theme }) => `
        margin-right: ${theme.tggSpacing(rem(3), 7.5)};
        margin-left: ${theme.tggSpacing(rem(3), 7.5)};

        ${theme.breakpoints.up('desktop')} {
            margin-right: ${theme.tggSpacing(0, 0)};
            margin-left: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledTimesAndLocationSection = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-shrink: 0;
        flex-direction: column;
        justify-content: space-evenly;
        padding-bottom: ${theme.tggSpacing(rem(3), 7.5)};
        margin-top: ${theme.tggSpacing(rem(3.8), 15)};
        text-align: center;
        ${theme.breakpoints.up('desktop')} {
            flex-direction: row;
        }
	`}
`;

export const StyledTimesAndLocationChild = styled('div')`
    display: flex;
`;

export const StyledOpeningTimes = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        margin-left: ${theme.tggSpacing(rem(3), 7.5)};

        ${theme.breakpoints.up('desktop')} {
            display: flex;
            flex: 1 0 25%;
            flex-direction: column;
            align-items: center;
            margin-top: ${theme.tggSpacing(0, 0)};
            margin-left: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledSubHeader = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        grid-row-start: 1;
        grid-row-end: 2;
        grid-column-start: 1;
        grid-column-end: span end;
        grid-column-gap: ${theme.tggSpacing(rem(0.7), 3)};
        align-items: center;
        margin-bottom: ${theme.tggSpacing(rem(1), 2.5)};

        ${theme.breakpoints.up('desktop')} {
            flex-direction: column;
        }
	`}
`;

export const StyledHeading = styled(Heading)`
    ${({ theme }: { theme: Theme }) => `
        padding-left: ${theme.tggSpacing(rem(3), 7.5)};

        ${theme.breakpoints.up('desktop')} {
            padding-left: ${theme.tggSpacing(0, 0)};
            text-align: center;
        }
	`}
`;

export const StyledSubHeading = styled(Heading)`
    ${({ theme }: { theme: Theme }) => `
        margin-right: ${theme.tggSpacing(0, 0)};
        margin-left: ${theme.tggSpacing(0, 0)};
	`}
`;

export const StyledOpen24Hours = styled(Paragraph, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<OpeningTimesProperties>`
    ${({ theme, $iconWidth }) => `
        display: flex;
        padding-left: ${$iconWidth + 11 + 'px'};
        ${theme.breakpoints.up('desktop')} {
            padding-left: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledOpeningTimeAndDayWrapper = styled(
    'div',
)<OpeningTimesProperties>`
    ${({ theme, $iconWidth }) => `
        display: flex;
        justify-content: flex-start;
        padding-left: ${$iconWidth + 9 + 'px'};

        ${theme.breakpoints.up('desktop')} {
            justify-content: space-evenly;
            padding-left: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledOpeningTimeAndDayContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        gap: ${theme.tggSpacing(rem(0.5), 1.25)};
	`}
`;

export const StyledOpeningTimesDay = styled(Paragraph)`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        justify-content: flex-start;
        width: ${theme.tggSpacing(rem(9), 22.5)};

        ${theme.breakpoints.up('desktop')} {
            width: ${theme.tggSpacing(['auto'], ['auto'])};
        }
	`}
`;

export const StyledOpeningTimesTime = styled(Paragraph)`
    ${({ theme }: { theme: Theme }) => `
        padding-left:  ${theme.tggSpacing(rem(0.75), 1.875)};
	`}
`;

export const StyledLocationContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        padding-left: ${theme.tggSpacing(rem(3), 7.5)};
        margin-top: ${theme.tggSpacing(rem(1.8), 7)};

        ${theme.breakpoints.up('desktop')} {
            flex: 1 0 25%;
            padding: ${theme.tggSpacing(0, 0)};
            margin-top: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledLocationAddressContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: start;
    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
        display: block;
    }
`;

export const StyledAddressLine = styled(Paragraph)`
    ${({ theme }: { theme: Theme }) => `
        padding-right: ${theme.tggSpacing(rem(3), 7.5)};
        padding-left: ${theme.tggSpacing(rem(3), 7.5)};
        ${theme.breakpoints.up('desktop')} {
            padding-right: ${theme.tggSpacing(0, 0)};
            padding-left: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledDirections = styled('div')`
    ${({ theme }: { theme: Theme }) => theme.typography.text_12};
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        padding-top: ${theme.tggSpacing(rem(1), 2.5)};
        padding-right: ${theme.tggSpacing(rem(3), 7.5)};
        padding-left: ${theme.tggSpacing(rem(3), 7.5)};
        
        
        ${theme.breakpoints.up('desktop')} {
            display: block;
            padding-right: ${theme.tggSpacing(0, 0)};
            padding-left: ${theme.tggSpacing(0, 0)};
            a {
                padding-left: ${theme.tggSpacing(rem(2), 5)};
            }
        }
	`}

    a {
        ${({ theme }: { theme: Theme }) => theme.typography.bodySmall};
    }
`;

export const StyledMapsContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        max-width: ${theme.tggSpacing(rem(64), 160)};
        margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};
	`}
`;

export const StyledStaticMap = styled('img')`
    width: 100%;
`;

export const StyledOffPeakTimesContainer = styled(
    'section',
)<OpeningTimesProperties>`
    ${({ theme, $iconWidth }) => `
        display: flex;
        flex-direction: column;
        align-items: start;
        padding-left: ${$iconWidth + 9 + 'px'};
        margin-top: ${theme.tggSpacing(rem(1.8), 7)};

        ${theme.breakpoints.up('desktop')} {
            flex: 1 0 auto;
            align-items: center;
            padding-left: ${theme.tggSpacing(0, 0)};
            margin: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledOffPeakTimesDay = styled(Paragraph)`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        width: ${theme.tggSpacing(rem(9), 22.5)};
	`}
`;

export const StyledOffPeakTimesTime = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        gap: ${theme.tggSpacing(rem(0.5), 1.25)};
	`}
`;

export const StyledOffPeakTimesSection = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        gap: ${theme.tggSpacing(rem(0.5), 1.25)};
        align-items: start;
        justify-content: center;
	`}
`;

export const StyledOffPeakTimesWrapper = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        gap: ${theme.tggSpacing(rem(0.5), 1.25)};
        align-items: start;
        margin-left: ${theme.tggSpacing(rem(3), 7.5)};

        ${theme.breakpoints.up('desktop')} {
            margin: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledRenderOffPeakTimesContainer = styled('section')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: row;
        gap: ${theme.tggSpacing(rem(0.5), 1.25)};
        align-items: center;
        justify-content: center;
	`}
`;

export const StyledOffPeakTimesText = styled('p')`
    ${({ theme }: { theme: Theme }) => `
        margin: ${theme.tggSpacing(0, 0)};
	`}
`;

export const StyledTabs = styled(Tabs)`
    ${({ theme }: { theme: Theme }) => `
        margin-inline: ${theme.tggSpacing(rem(1.5), 3.75)};
        margin-top: ${theme.tggSpacing(rem(1), 2.5)};
	`}
`;
